// eslint-disable-next-line max-classes-per-file
import { references } from "common/helpers/references";
import { _ } from "extensions/underscore";
import { captureException } from "extensions/sentry";

import { loadExternalScript } from "common/utils";
import { store } from "common/store";
import { emitter } from "common/emitter";

import { request } from "common/components/request";
import { watch } from "common/components/watch";

const DEBOUNCE_TRACK = 1000;

const _debounceChecker = {};
const adapters = {};

/**
 * Pazien API tracking adapter
 */
adapters.PazienApiTracking = class {
    constructor() {
        this.#eventProperties = {};
        this.#supportedEvents = [
            "Page ready",
            "SAML error",
            "SAML success"
        ];
    }
    
    #supportedEvents = [];
    
    #eventProperties = {};
    
    #error = false;
    
    loaded() {
        return !this.#error;
    }
    
    track(event, properties) {
        // We only want to track certain events for now
        if (!this.#supportedEvents.includes(event)) return;
        
        request("/tracking", {
            method: "POST",
            data: {
                event,
                properties: {
                    ...this.#eventProperties,
                    ...properties
                }
            }
        }).catch(() => {
            // Don't raise an exception on tracking failure
        });
    }
    
    addEventProperties(properties = {}) {
        if (_.isObject(properties)) {
            this.#eventProperties = {
                ...this.#eventProperties,
                ...properties
            };
        }
    }
    
    identify(userId, traits) {
        // We can get user info from a header
        return this;
    }
};

adapters.SessionTracker = class {
    constructor() {
        try {
            import("common/components/beacon/").then(({ Beacon }) => {
                this.Beacon = Beacon;
                this.Beacon.configure({
                    api: store.getState().config.api_host,
                    id: "merchant"
                });
            }).catch((error) => {
                this.#error = true;
            });
        } catch {
            this.#error = true;
        }
    }
    
    #ignoreEvents = [
        "Page ready"
    ];
    
    #error = false;
    
    loaded() {
        return !this.#error;
    }
    
    track(event, properties = {}) {
        if (this.#ignoreEvents.includes(event)) return;
    
        const beacon = new this.Beacon(event, properties);
        beacon.send();
    }
    
    identify(user_ref) {
        this.Beacon.identify(user_ref);
    }
    
    addEventProperties(properties = {}) {
        this.Beacon.setMerchant(properties.merchant_id);
    }
};

adapters.Beamer = class {
    constructor() {
        window.beamer_config = {
            product_id: "kQFzsDAJ8906",
            lazy: true,
            button: false,
            selector: ".beamer-announcements",
            embed: true,
            alert: false
        };
        loadExternalScript("https://app.getbeamer.com/js/beamer-embed.js").then(() => {
            this.Beamer = window.Beamer;
        }).catch((error) => {
            // Die without disturbing peaceful Pazien app ;)
            this.#error = true;
        });
    }
    
    #error = false;
    
    loaded() {
        return !this.#error;
    }
    
    identify(userId, traits) {
        if (this.Beamer) {
            window.beamer_config = {
                ...window.beamer_config,
                user_is_admin: traits.admin,
                user_id: userId,
                user_is_wp: traits.merchant_ref === store.getState().config.worldpayMerchant,
                merchant_ref: traits.merchant_ref,
                sso_provider: traits.sso_provider
            };
        }
    }
};

adapters.AppCues = class {
    constructor() {
        this.Appcues = null;
        this.#supportedEvents = [
            "navigate"
        ];
        
        loadExternalScript("https://fast.appcues.com/29097.js").then(() => {
            this.Appcues = window.Appcues;
        }).catch((error) => {
            // Die without disturbing peaceful Pazien app ;)
            this.#error = true;
        });
    }
    
    #supportedEvents = [];
    
    #error = false;
    
    loaded() {
        return !this.#error;
    }
    
    track(event, properties) {
        if (!this.Appcues) return;
        
        // We only want to track certain events for now
        if (this.#supportedEvents.includes(event)) {
            try {
                this.Appcues.page();
            } catch {
                // utils.captureException(err);
            }
        }
    }
    
    identify(userId, traits) {
        const base = {
            email: traits.username,
            first_name: traits.first_name,
            last_name: traits.last_name,
            wp_sso_user: traits.sso_user && traits.sso_provider === "worldpay",
            sso_provider: traits.sso_provider,
            superuser: traits.superuser,
            admin: traits.admin,
            merchant_ref: traits.merchant_ref
        };
       
        if (this.Appcues) {
            this.Appcues.identify(userId, base);
        }
        const unsubscribe = store.subscribe(watch(store.getState, "common.merchant_config.package_ref")((new_package_ref) => {
            if (this.Appcues) {
                const packageInfo = references.toAlias(new_package_ref, "package");
                if (this.Appcues) {
                    this.Appcues.identify(userId, {
                        ...base,
                        packageName: packageInfo?.display_name,
                        packagePartner: packageInfo?.partner
                    });
                }
            }
            unsubscribe();
        }));
        
        const unsubscribe2 = store.subscribe(watch(store.getState, "common.merchant_dimensions.data.provider")((providers) => {
            if (this.Appcues) {
                const providerObj = {
                    has_Adyen: providers.includes("adyen"),
                    has_Amex: providers.includes("amex"),
                    has_Chase: providers.includes("chase"),
                    has_Discover: providers.includes("discover"),
                    has_FirstData: providers.includes("firstdata"),
                    has_Fraudsight: providers.includes("fraudsight"),
                    has_FraudsightUS: providers.includes("fraudsightus"),
                    has_Nap: providers.includes("nap"),
                    has_Paypal: providers.includes("paypal"),
                    has_ThreeDSFlex: providers.includes("threedsflex"),
                    has_Vantiv: providers.includes("vantiv"),
                    has_VantivCore: providers.includes("vantivcore"),
                    has_WPG: providers.includes("wpg")
                };
                this.Appcues.identify(userId, {
                    ...providerObj
                });
            }
            unsubscribe2();
        }));
    }
};

/**
 * Tracking constructor
 * @param options
 * @constructor
 */
class Tracking {
    constructor(options) {
        this.#integrations = [];
    
        emitter.on("tracking.identify", (user) => {
            this.identify(user._id, user);
        });
    
        emitter.on("tracking.track", ({ type, data }) => {
            this.track(type, data);
        });
        
        this.options = options;
    }
    
    #integrations = null;
    
    runForEachIntegration(method, ...args) {
        this.#integrations.forEach((adapter) => {
            try {
                if (adapter.loaded() && typeof adapter[method] === "function") {
                    adapter[method](...args);
                }
            } catch (error) {
                // do not propagate the exception, just capture in Sentry
                captureException(error);
            }
        });
    }
    
    addIntegration(name, options) {
        const Adapter = adapters[name];
        
        if (!Adapter) {
            throw new Error("Requested adapter doesn't exist");
        }
        
        this.#integrations.push(new Adapter(options));
    }
    
    addEventProperties(properties) {
        this.runForEachIntegration("addEventProperties", properties);
    }
    
    /**
     * Record user event
     * @param {string} event The name of the event you’re tracking.
     * @param {object | function} [properties] A dictionary of properties for the event.
     */
    track(event, properties) {
        if (!event) return;
        
        if (typeof properties === "function") {
            try {
                properties = properties();
            } catch {
                properties = {};
            }
        }
        
        // do not debounce the navigate.  Need current url for the AppCues flows.
        const lastTimeCalledWithThisEvent = event !== "navigate" && _debounceChecker[event];
        const now = Date.now();
        
        if (!lastTimeCalledWithThisEvent || (now - lastTimeCalledWithThisEvent > DEBOUNCE_TRACK)) {
            _debounceChecker[event] = now;
            
            this.runForEachIntegration("track", event, properties);
        }
    }
    
    /**
     * Tie one of the users and their actions to a recognizable userId
     * @param {string} [userId] The database ID for the user.
     * @param {object} [traits] A dictionary of traits you know about the user, like their email or name.
     */
    identify(userId, traits) {
        this.runForEachIntegration("identify", userId, traits);
    }
}

export const tracking = new Tracking();
